import React from 'react';
import { withTranslation } from 'react-i18next';
import { initSelectors } from '../../../behavior/init/index';
import { Popup, Confirm, Button } from 'semantic-ui-react';
import { ChemicalProduct, Inventory } from '../../../routes';
import {
  Mode,
  entityType as et,
  recordOptionType,
  HISTORY_OVERVIEW_TYPE,
  MOBILE,
} from '../../../constants';
import PropTypes from 'prop-types';
import connect from 'react-redux/es/connect/connect';
import { compose } from 'redux';
import { history } from '../../../store';
import InventoryOverviewPopup from './InventorySimplifiedView/InventoryOverviewPopup';
import useUser from '@behavior/init/getUserV2';
import { QueryKey } from '@behavior/QueryKey';
import { showEditDeleteButton } from '@behavior/init/selectors';

const RecordOptions = ({
  entityType,
  entityId,
  parentEntityId,
  deleteHandler,
  displayTableOptions,
  entityCreator,
  entityResponsible,
  entityDepartment,
  handleDefaultFilteredListOverride,
  t,
}) => {
  const { user } = useUser();
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [confirmMessage, setConfirmMessage] = React.useState('');
  const [confirmEntityId, setConfirmEntityId] = React.useState(null);
  const [isDelete, setIsDelete] = React.useState(false);

  const handleConfirm = () => {
    if (isDelete === true) {
      deleteHandler(confirmEntityId);
    }
    setConfirmOpen(false);
    setConfirmMessage('');
    setConfirmEntityId(null);
    setIsDelete(false);
  };
  const handleCancel = () => {
    setConfirmOpen(false);
    setConfirmMessage('');
    setConfirmEntityId(null);
    setIsDelete(false);
  };

  const redirectHandler = (entityType, mode) => {
    handleDefaultFilteredListOverride();
    if (et.inventory === entityType) {
      history.push(Inventory.baseName + `/${mode}` + (entityId ? `/${entityId}` : `/:id`));
      if (mode === MOBILE) {
        history.push(Inventory.baseName + `/${mode}` + (entityId ? `/${entityId}` : `/:id`));
      }
    } else if (et.product === entityType) {
      history.push(ChemicalProduct.baseName + `/${mode}` + (entityId ? `/${entityId}` : `/:id`));
    } else if (et.building === entityType) {
      history.push(Inventory.baseName + `/${mode}` + (entityId ? `/${entityId}` : `/:id`));
    } else if (et.productHist === entityType && mode === Mode.VIEW) {
      history.push(
        ChemicalProduct.baseName + `/${mode}/history` + (entityId ? `/${entityId}` : `/:id`)
      );
    } else if (et.inventoryHist === entityType) {
      if (mode === Mode.VIEW)
        history.push(
          Inventory.baseName + `/${mode}/history` + (entityId ? `/${entityId}` : `/:id`)
        );
      else if (mode === Mode.UNDO_DELETE)
        history.push(
          Inventory.baseName + `/${mode}` + (parentEntityId ? `/${parentEntityId}` : `/:id`)
        );
    } else if (et.activity === entityType) {
      history.push(`/${QueryKey.ACTIVITY}/${mode}/${entityId}`);
    }
  };

  const renderOptions = props => {
    let options = [];
    let isCanModifyAsCreator = initSelectors.isCanModifyAsCreator(
      entityType,
      user,
      entityResponsible,
      entityCreator,
      entityDepartment
    );
    let isEditor = initSelectors.isEditor(user);
    let isCanModifyEntityOfHistoryAsCreator = initSelectors.isCanModifyAsCreator(
      initSelectors.getEntityTypeBasedOnHistEntityType(entityType),
      user,
      entityResponsible,
      entityCreator
    );

    const createPopupButton = (key, color, icon, tooltipKey, onClickHandler) => (
      <Popup
        key={key}
        trigger={
          <Button
            type="button"
            color={color}
            icon={icon}
            className="mouseCursorPointer"
            disabled={false}
            onClick={onClickHandler}
          />
        }
        size="mini"
        content={t(tooltipKey)}
        position="top left"
      />
    );

    displayTableOptions.forEach(option => {
      const key = `${entityId}${option}`;
      let button;

      switch (option) {
        case recordOptionType.edit:
          if (isCanModifyAsCreator && showEditDeleteButton(entityType, user)) {
            button = createPopupButton(
              key,
              'blue',
              'edit',
              'inventory.overview.page.table.container.edit.record.tooltip',
              () => redirectHandler(entityType, Mode.EDIT)
            );
          }
          break;

        case recordOptionType.view:
          button = (
            <InventoryOverviewPopup
              key={key}
              entityType={entityType}
              handleDefaultFilteredListOverride={handleDefaultFilteredListOverride}
              entityId={entityId}
              redirectHandler={redirectHandler}
              isCanModifyAsCreator={isCanModifyAsCreator}
              isEditor={isEditor}
            />
          );
          break;

        case recordOptionType.undoDelete:
          if (isCanModifyEntityOfHistoryAsCreator) {
            button = createPopupButton(
              key,
              'red',
              'undo',
              'inventory.overview.page.table.container.undo.delete.record.tooltip',
              () => redirectHandler(entityType, Mode.UNDO_DELETE)
            );
          }
          break;

        case recordOptionType.clone:
          if (entityType !== et.activity || initSelectors.isActivityCanCreate(user, entityType)) {
            button = createPopupButton(
              key,
              'blue',
              'clone',
              'inventory.overview.page.table.container.clone.record.tooltip',
              () => redirectHandler(entityType, Mode.CLONE)
            );
          }
          break;

        case recordOptionType.history:
          button = createPopupButton(
            key,
            'blue',
            'history',
            'inventory.overview.page.table.container.history.record.tooltip',
            () => redirectHandler(entityType, HISTORY_OVERVIEW_TYPE)
          );
          break;

        case recordOptionType.delete:
          if (isCanModifyAsCreator && showEditDeleteButton(entityType, user)) {
            button = createPopupButton(
              key,
              'red',
              'remove',
              'inventory.overview.page.table.container.delete.record.tooltip',
              () => {
                setConfirmOpen(true);
                setConfirmMessage('Please confirm in order to remove this record');
                setConfirmEntityId(entityId);
                setIsDelete(true);
              }
            );
          }
          break;

        default:
          break;
      }

      if (button) {
        options.push(button);
      }
    });

    return options;
  };

  return (
    <div>
      <Confirm
        open={confirmOpen}
        content={confirmMessage}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
      />
      <Button.Group size="mini">{renderOptions()}</Button.Group>
    </div>
  );
};
RecordOptions.propTypes = {
  entityId: PropTypes.string.isRequired,
  entityCreator: PropTypes.string.isRequired,
  entityResponsible: PropTypes.array.isRequired,
  entityDepartment: PropTypes.string.isRequired,
  entityType: PropTypes.string.isRequired,
  deleteHandler: PropTypes.func,
  displayTableOptions: PropTypes.array,
  updateTemporarySavedFilters: PropTypes.func,
};

RecordOptions.defaultProps = {
  displayTableOptions: [],
  deleteHandler: () => {},
  initialData: {},
};

const mapDispatchToProps = dispatch => ({
  getInitialData: (isCreateMode, isCloneMode) =>
    dispatch(inventoriesActions.getInitialData(isCreateMode, isCloneMode)),
});

const mapStateToProps = (state, ownProps) => ({
  ...state.inventories,
  id: ownProps.entityId,
});

export default compose(
  withTranslation(),
  connect(mapStateToProps, mapDispatchToProps)
)(RecordOptions);
